import { ChevronLeft } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CModal from "../../Components/CModal/CModal";
import CToast from "../../Components/CToast/CToast";
import { CheckoutState } from "../../Models/Banks";
import {
  isRecurringInstructFail,
  isRecurringInstructSuccess,
  RecurringInstructStatus,
} from "../../Models/RecurringInstructStatus";
import { ROUTE_PATHS } from "../../Routes/constants";
import { masterpassRecurringCheck } from "../../Services/BankService";
import { getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";

const POLL_INTERVAL_MS = 4000;

const MasterpassPage = () => {
  const { state }: CheckoutState = useLocation();
  const response = state?.response;
  const referrer = state?.referrer;
  const navigate = useNavigate();
  const setRecurringCompleted = useAuthState((s) => s.setRecurringCompleted);

  const [errorModalOpen, setErrorModalOpen] = useState(false);


  const shouldPollRef = useRef(true);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRef = useRef(response?.token);
  tokenRef.current = response?.token;

  useEffect(() => {
    if (!response?.url) {
      navigate(ROUTE_PATHS.CHECKOUT);
    }
  }, [response?.url, navigate]);

  const stopPolling = useCallback(() => {
    shouldPollRef.current = false;
    if (pollTimeoutRef.current != null) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, [pollTimeoutRef]);

  const navigateBack = useCallback(() => {
    stopPolling();
    navigate(referrer);
  }, [navigate, stopPolling, referrer]);

  const handleUserGoBack = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  useEffect(() => {
    if (!response?.url) {
      return;
    }
    if (!response?.token) {
      return;
    }

    shouldPollRef.current = true;


    const handleInstructionStatus = (
      status: RecurringInstructStatus | undefined,
      scheduleNextPoll: () => void
    ) => {
      if (status === undefined) {
        scheduleNextPoll();
        return;
      }
      if (isRecurringInstructFail(status)) {
        stopPolling();
        setErrorModalOpen(true);
      } else if (isRecurringInstructSuccess(status)) {
        stopPolling();
        setRecurringCompleted(true);
        CToast(getRemoteValue("recurring_instruction_success"), "success");
        navigateBack();
      } else {
        scheduleNextPoll();
      }
    };

    const poll = async () => {
      if (!shouldPollRef.current) {
        return;
      }
      const token = tokenRef.current;
      if (!token) {
        return;
      }

      try {
        const status = await masterpassRecurringCheck(token);
        if (!shouldPollRef.current) {
          return;
        }

        const scheduleNextPoll = () => {
          pollTimeoutRef.current = setTimeout(() => {
            void poll();
          }, POLL_INTERVAL_MS);
        };

        handleInstructionStatus(status, scheduleNextPoll);
      } catch {
        // handleError already invoked in BankService
      }
    };

    pollTimeoutRef.current = setTimeout(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      stopPolling();
    };
  }, [response?.url, response?.token, navigate, setRecurringCompleted, stopPolling, navigateBack]);

  const isTestEnvironment = process.env.REACT_APP_NODE_ENV === "stage";

  return (
    <div className="md:!px-[5%] lg:!px-[8%]">
      <Box sx={{ mb: 2, pt: 2 }}>
        <Button
          onClick={handleUserGoBack}
          startIcon={<ChevronLeft />}
          sx={{
            color: '#191B1E',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(25, 27, 30, 0.04)',
            },
            pl: 1
          }}
        >
          {getRemoteValue("go_back")}
        </Button>
      </Box>
      {response?.url ? (
        <iframe
          src={response.url}
          style={{ height: "calc(100vh - 56px)", width: "100%" }}
          title="Iframe Masterpass"
          {...(isTestEnvironment && { allow: "local-network-access" })}
        ></iframe>
      ) : null}
      <CModal open={errorModalOpen} onClose={navigateBack}>
        <Stack spacing={2}>
          <Typography variant="h6">{getRemoteValue("recurring_instruction_error_title")}</Typography>
          <Typography variant="body2">{getRemoteValue("recurring_instruction_error_description")}</Typography>
          <Box>
            <Button variant="contained" onClick={navigateBack} fullWidth>
              {getRemoteValue("recurring_instruction_fail_back")}
            </Button>
          </Box>
        </Stack>
      </CModal>
    </div>
  );
};

export default MasterpassPage;
